import type { CriminalRequestViewData } from '../services/criminalCaseRequests';
import type { CaseRequestHeaderResponse, WorkflowProgressStage } from '../types/caseRequestDetails';
import type { CriminalSaveStep1, CriminalSaveStep3, CriminalSaveStep4 } from '../types/criminalApi';
import type { CriminalCaseDetail, TaskWorkflowStep, WfNodeKind } from '../data/taskCaseDetails';
import { statusPillClass } from '../data/taskCaseDetails';
import { calcAgeFromDob } from './caseFieldValidation';
import { deliveryMethodDisplay } from './deliveryMethodDisplay';
import { formatDisplayDate, formatDisplayDateTime } from './dateFormat';

function str(...values: (string | undefined | null)[]): string {
  for (const v of values) {
    const t = typeof v === 'string' ? v.trim() : '';
    if (t) return t;
  }
  return '';
}

export function headerReferenceNo(h: CaseRequestHeaderResponse): string {
  return str(h.referenceNo, h.reference_no);
}

export function headerCategory(h: CaseRequestHeaderResponse): string {
  return str(h.category);
}

export function headerSubCategory(h: CaseRequestHeaderResponse): string {
  return str(h.subCategory, h.sub_category);
}

export function headerCurrentStage(h: CaseRequestHeaderResponse): string {
  return str(h.currentStage, h.current_stage);
}

export function headerTaskStatus(h: CaseRequestHeaderResponse): string {
  return str(h.taskStatus, h.task_status);
}

export function headerTaskType(h: CaseRequestHeaderResponse): string {
  return str(h.taskType, h.task_type);
}

export function headerPhysicalFileCustody(h: CaseRequestHeaderResponse): string {
  return str(h.physicalFileCustody, h.physical_file_custody);
}

export function headerLinkedFileRef(h: CaseRequestHeaderResponse): string {
  return str(h.linkedFileRefNo, h.linked_file_ref_no);
}

export function headerIsReceivedViaForward(h: CaseRequestHeaderResponse): boolean {
  return h.isReceivedViaForward === true || h.is_received_via_forward === true;
}

export function headerTaskCreatedById(h: CaseRequestHeaderResponse): string {
  return str(h.taskCreatedById, h.task_created_by_id);
}

export function headerTaskCreatedByName(h: CaseRequestHeaderResponse): string {
  return str(h.taskCreatedByName, h.task_created_by_name);
}

export function custodyPillClass(custody: string): string {
  const s = custody.toLowerCase();
  if (s.includes('custody') || s.includes('in custody')) return 'is-custody';
  if (s.includes('pending')) return 'is-pending';
  return 'is-default';
}

export function isRejectedTaskStatus(status: string): boolean {
  return status.toLowerCase().includes('reject');
}

function normalizeTaskStatusLabel(status: string): string {
  return status.trim().toLowerCase().replace(/[_-]/g, ' ').replace(/\s+/g, ' ');
}

export function isNotStartedTaskStatus(status: string): boolean {
  return normalizeTaskStatusLabel(status) === 'not started';
}

/**
 * True only when header task status is In Progress.
 * Used to unlock Case Update / tab edits and workflow actions (0002252).
 */
export function isInProgressTaskStatus(status: string): boolean {
  const s = normalizeTaskStatusLabel(status);
  return s === 'in progress' || s.includes('in progress');
}

/** Case Update and other tab fields stay read-only until status is In Progress. */
export function areTaskCaseTabsEditable(taskStatus: string): boolean {
  return isInProgressTaskStatus(taskStatus);
}

export function isOnHoldTaskStatus(status: string): boolean {
  const s = normalizeTaskStatusLabel(status);
  return s === 'on hold' || s.includes('on hold');
}

export function isClosureStage(stage: string): boolean {
  return stage.toLowerCase().includes('closure');
}

function stageSequence(s: WorkflowProgressStage): number {
  return s.sequence_number ?? s.sequenceNumber ?? 0;
}

function stageIsCurrent(s: WorkflowProgressStage): boolean {
  return s.is_current === true || s.isCurrent === true;
}

/** Stage immediately after the one marked `is_current` (CMS-CRC-21 workflow progress). */
export function getNextWorkflowStageName(stages: WorkflowProgressStage[] | undefined): string {
  if (!stages?.length) return '';
  const ordered = [...stages].sort((a, b) => stageSequence(a) - stageSequence(b));
  const currentIdx = ordered.findIndex(stageIsCurrent);
  if (currentIdx < 0) return '';
  const next = ordered[currentIdx + 1];
  if (!next) return '';
  return str(next.name);
}

export function isNextWorkflowStageClosure(stages: WorkflowProgressStage[] | undefined): boolean {
  return isClosureStage(getNextWorkflowStageName(stages));
}

/** True when CMS-CRC-21 workflow progress marks Closure as the current stage (`is_current`). */
export function isCurrentWorkflowStageClosure(stages: WorkflowProgressStage[] | undefined): boolean {
  if (!stages?.length) return false;
  const current = stages.find(stageIsCurrent);
  if (!current) return false;
  return isClosureStage(str(current.name));
}

/** Map CMS-CRC-21 stages → workflow sidebar nodes (completed / current / pending). */
export function mapWorkflowStagesToSteps(stages: WorkflowProgressStage[] | undefined): TaskWorkflowStep[] {
  if (!stages?.length) return [];

  const ordered = [...stages].sort((a, b) => stageSequence(a) - stageSequence(b));
  const currentIdx = ordered.findIndex(stageIsCurrent);

  return ordered.map((stage, index) => {
    let kind: WfNodeKind;
    if (currentIdx < 0) {
      kind = index === ordered.length - 1 ? 'curr' : 'done';
    } else if (index < currentIdx) {
      kind = 'done';
    } else if (index === currentIdx) {
      kind = 'curr';
    } else {
      kind = 'pend';
    }

    const title = str(stage.name) || `Stage ${index + 1}`;
    return {
      kind,
      title,
      line1: kind === 'done' ? 'Completed' : kind === 'curr' ? 'In progress' : '',
      line2: kind === 'pend' ? 'Pending' : '',
    };
  });
}

function suspectStatusForName(step3: CriminalSaveStep3 | undefined, name: string): string {
  const suspects = step3?.suspects;
  if (!Array.isArray(suspects)) return '';
  for (const s of suspects) {
    if (typeof s !== 'object' || !s) continue;
    if ((s.suspect_name ?? '').trim() === name.trim()) {
      return (s.suspect_status_name ?? '').trim();
    }
  }
  return '';
}

function accusedDisplay(
  draft: CriminalRequestViewData['draft'],
  step3: CriminalSaveStep3 | undefined,
): CriminalCaseDetail['accused'] {
  return draft.accused
    .filter((a) => a.name.trim() || a.status.trim() || a.statusOther.trim())
    .map((a) => {
      const statusName =
        a.statusOther.trim() ||
        suspectStatusForName(step3, a.name) ||
        step3?.suspect_status_name?.trim() ||
        a.status;
      return { name: a.name.trim(), status: statusName.trim() };
    });
}

function urgencyLabel(step3: CriminalSaveStep3 | undefined, draft: CriminalRequestViewData['draft']): string {
  return str(step3?.urgency_level_name) || draft.urgencyLevelId || '—';
}

function offenceDateDisplay(step3: CriminalSaveStep3 | undefined, draft: CriminalRequestViewData['draft']): string {
  const from = formatDisplayDate(step3?.date_of_offence_from ?? draft.offenceDateFrom);
  const to = formatDisplayDate(step3?.date_of_offence_to ?? draft.offenceDateTo);
  if (from && to && from !== to) return `${from} - ${to}`;
  return from || to || '—';
}

/** Map CMS-CRC-1 criminal steps view → Case Details tab model. */
export function mapCriminalViewToCaseDetail(view: CriminalRequestViewData): CriminalCaseDetail {
  const { draft, step1, step2, step3, step4 } = view;
  const s1 = step1 as CriminalSaveStep1 | undefined;
  const victimDobRaw = step2?.date_of_birth ?? draft.victimDob;
  const victimDob = formatDisplayDate(victimDobRaw) || victimDobRaw || '—';
  const computedAge = calcAgeFromDob(victimDobRaw);
  const victimAge =
    typeof step2?.age === 'number' && Number.isFinite(step2.age) ? step2.age : (computedAge ?? 0);

  const delivery = deliveryMethodDisplay(draft, [], step4);

  const hasConnected =
    s1?.has_connected_case === true
      ? 'Yes'
      : s1?.has_connected_case === false
        ? 'No'
        : draft.hasConnectedMatters === 'yes'
          ? 'Yes'
          : draft.hasConnectedMatters === 'no'
            ? 'No'
            : 'No';

  return {
    fileCategory: str(s1?.category_name, draft.fileCategory) || '—',
    subFileCategory: str(s1?.sub_category_name, draft.subFileCategoryName) || '—',
    applicableAreas: draft.applicableAreas.length ? draft.applicableAreas : ['—'],
    description: draft.description.trim() || '—',
    caseSource: str(s1?.case_source_name, draft.caseSource, draft.caseSourceOther) || '—',
    province: str(s1?.province_name) || '—',
    district: str(s1?.district_name) || '—',
    highCourtName: str(s1?.high_court_name) || '—',
    magistrateCourtName: str(s1?.magistrate_court_name) || '—',
    policeStation: str(s1?.police_station_name) || '—',
    yourReferenceNumber: draft.yourReferenceNumber.trim() || '—',
    otherExternalReferences: draft.otherExternalReferences.trim() || '—',
    caseNumber: draft.caseNumber.trim() || '—',
    hasConnectedMatters: hasConnected,
    connectedCaseNumber: draft.connectedCaseNumber.trim() || '—',
    connectedAgdFileRef: draft.connectedAgdFileRef.trim() || '—',
    victimName: str(step2?.name_of_victim, draft.victimName) || '—',
    victimDob,
    victimAge,
    victimGender: str(step2?.gender_name, draft.victimGender) || '—',
    offenceDate: offenceDateDisplay(step3, draft),
    placeOfOffence: str(step3?.place_of_offence, draft.placeOfOffence) || '—',
    accused: accusedDisplay(draft, step3),
    urgencyLevel: urgencyLabel(step3, draft),
    deadline: formatDisplayDate(step3?.deadline ?? draft.deadline) || '—',
    urgencyJustification: str(step3?.urgency_justification, draft.urgencyJustification) || '—',
    submittedAgencyName: str(step4?.submitted_agency_name) || '—',
    submittedDivision: str(step4?.submitted_division_name) || '—',
    designation: str(step4?.designation) || '—',
    submittedBy: str(step4?.submitted_by_display_name) || '-',
    email: str(step4?.email) || '—',
    contactNumber: str(step4?.contact_no) || '—',
    deliveryMethod: delivery,
  };
}

export function formatSubmittedAt(apiValue: string | undefined, fallback = '—'): string {
  return formatDisplayDateTime(apiValue ?? '') || fallback;
}

export { statusPillClass };
